import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { House, MapPin, Microphone, CalendarBlank, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isNative, nativeHaptic } from "@/lib/native";
import MemoRecorder from "@/pages/agenda/MemoRecorder";

/**
 * BottomTabBar — Tab bar nativa iOS-style fissa in basso, visibile SOLO su
 * mobile native (Capacitor) — su desktop l'utente ha già la sidebar.
 *
 * iter v30 r135 — Smart Mic universale.
 *   Tap sul microfono centrale → apre MemoRecorder in routeMode="assistant".
 *   L'utente parla. Il backend trascrive e CAPISCE automaticamente se è:
 *     • una domanda all'Assistente   → naviga a /dashboard/assistente
 *     • un promemoria/appuntamento  → mostra anteprima agenda → salva
 *   Niente long-press, niente scelte: un gesto solo, l'AI smista.
 *
 *   L'Agenda resta accessibile sempre dalla tab dedicata (CalendarBlank
 *   a destra del mic). Chi vuole entrare nella vista calendario lo fa lì.
 *
 * Safe-area-inset-bottom rispettato (home-indicator iPhone).
 */
export default function BottomTabBar() {
  const location = useLocation();
  const [memoOpen, setMemoOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  // iter v32 r24 — User: «la barra Home/Sopralluogo/… rimane visibile
  // quando modifico il testo con tastiera aperta e dà fastidio». Su
  // Capacitor iOS la tab bar `fixed` non viene spinta dalla keyboard:
  // ci abboniamo agli eventi `keyboardWillShow`/`keyboardWillHide` del
  // plugin nativo e nascondiamo la tab bar interamente mentre l'utente
  // scrive. Fallback web: ascolta focus/blur sui campi editabili.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  // iter v32 r27 — User: «la barra Home/Sopralluogo… dà troppo fastidio.
  // Mettila a scomparsa o che si possa alzare». Implemento auto-hide su
  // scroll giù (chiunque legge contenuto scrolla verso il basso → nascondi
  // la barra per dare più spazio) + ricomparsa su scroll su (cerca di
  // tornare alla nav).
  const [scrolledHidden, setScrolledHidden] = useState(false);
  useEffect(() => {
    let lastY = 0;
    let lastChangeAt = 0;
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const dy = y - lastY;
      const now = Date.now();
      // Debounce per evitare di flickerare (cambio max ogni 200ms)
      if (now - lastChangeAt < 200) { lastY = y; return; }
      if (dy > 8 && y > 100) {
        // scroll DOWN deciso → nascondi
        if (!scrolledHidden) { setScrolledHidden(true); lastChangeAt = now; }
      } else if (dy < -8) {
        // scroll UP → mostra
        if (scrolledHidden) { setScrolledHidden(false); lastChangeAt = now; }
      }
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrolledHidden]);

  // Mostra hint "il microfono capisce anche promemoria" solo la PRIMA volta
  // che l'utente è sulla tab bar (chiave nuova così appare anche a chi
  // aveva già visto l'hint long-press precedente).
  useEffect(() => {
    if (!isNative) return;
    try {
      const seen = localStorage.getItem("pronto:tabbar:smart-mic-hint-seen");
      if (!seen) {
        const t = setTimeout(() => setShowHint(true), 1500);
        const hide = setTimeout(() => {
          setShowHint(false);
          localStorage.setItem("pronto:tabbar:smart-mic-hint-seen", "1");
        }, 7500);
        return () => { clearTimeout(t); clearTimeout(hide); };
      }
    } catch { /* ignore */ }
  }, []);

  // iter v32 r24 — Listener keyboard: native (Capacitor) + fallback web.
  useEffect(() => {
    let unsub = () => {};
    (async () => {
      if (isNative) {
        try {
          const { Keyboard } = await import("@capacitor/keyboard");
          const subShow = await Keyboard.addListener("keyboardWillShow", () => setKeyboardOpen(true));
          const subHide = await Keyboard.addListener("keyboardWillHide", () => setKeyboardOpen(false));
          unsub = () => {
            try { subShow.remove(); } catch { /* */ }
            try { subHide.remove(); } catch { /* */ }
          };
          return;
        } catch { /* plugin assente: fallback web */ }
      }
      // Web fallback: focusin/focusout su input/textarea/contenteditable
      const onFocus = (e) => {
        const t = e.target;
        if (!t) return;
        const tag = (t.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || t.isContentEditable) {
          setKeyboardOpen(true);
        }
      };
      const onBlur = () => setKeyboardOpen(false);
      document.addEventListener("focusin", onFocus);
      document.addEventListener("focusout", onBlur);
      unsub = () => {
        document.removeEventListener("focusin", onFocus);
        document.removeEventListener("focusout", onBlur);
      };
    })();
    return () => unsub();
  }, []);

  if (!isNative) return null;

  // iter v32 r30 — Nascondi la tab bar su Assistente: il bottone Microphone
  // centrale (translate-y-3, ring-4) sfora sopra la linea della tab bar e si
  // sovrappone all'input chat. Su questa pagina l'utente sta facendo una
  // conversazione lunga: la nav globale distrae. Esce con il back nativo iOS.
  const path = location.pathname || "";
  if (path.startsWith("/dashboard/assistente")) return null;
  // iter v32 r24 — Nascondi durante editing testo (vedi nota sopra).
  if (keyboardOpen) return null;

  const tabs = [
    { to: "/dashboard",             icon: House,         label: "Home",        testid: "tab-home" },
    { to: "/dashboard/sopralluogo", icon: MapPin,        label: "Sopralluogo", testid: "tab-sopralluogo" },
    { to: "#mic",                   icon: Microphone,    label: "Assistente",  testid: "tab-assistente", primary: true },
    { to: "/dashboard/agenda",      icon: CalendarBlank, label: "Agenda",      testid: "tab-agenda" },
    { to: "/dashboard/profile",     icon: UserCircle,    label: "Profilo",     testid: "tab-profilo" },
  ];

  const isActive = (to) =>
    to === "/dashboard"
      ? location.pathname === to
      : to !== "#mic" && (location.pathname === to || location.pathname.startsWith(`${to}/`));

  const openSmartMic = () => {
    nativeHaptic("medium").catch(() => {});
    setMemoOpen(true);
    try { localStorage.setItem("pronto:tabbar:smart-mic-hint-seen", "1"); } catch { /* */ }
    setShowHint(false);
  };

  return (
    <>
      {/* iter v30 r135 — Tooltip onboarding: spiega che il mic capisce
          domande E promemoria. Mostrato 1 sola volta. */}
      {showHint && (
        <div
          data-testid="tabbar-smart-mic-hint"
          className="lg:hidden fixed left-1/2 -translate-x-1/2 z-50 bottom-24 max-w-[290px] bg-[#1C1C1A] text-white rounded-2xl px-3.5 py-2.5 shadow-xl"
          style={{ paddingBottom: "calc(10px + env(safe-area-inset-bottom))" }}
        >
          <div className="text-[12.5px] font-semibold leading-snug">
            Tocca il microfono per parlare con l&apos;<span className="text-[#C8B886]">Assistente</span>.
            Capisce anche promemoria e appuntamenti — li aggiungo io in Agenda.
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-4 h-4 bg-[#1C1C1A] rotate-45" />
        </div>
      )}

      <nav
        data-testid="bottom-tab-bar"
        className={`lg:hidden fixed inset-x-0 bottom-0 z-40 bg-[#FAFAF7]/95 backdrop-blur border-t border-[#EAE7DF] transition-transform duration-300 ${
          scrolledHidden ? "translate-y-full" : "translate-y-0"
        }`}
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 6px)" }}
      >
        <ul className="flex items-stretch justify-around">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = isActive(t.to);
            if (t.primary) {
              // Bottone centrale: NON naviga, apre la modal Smart Mic.
              return (
                <li key={t.to} className="flex-1 flex justify-center">
                  <button
                    type="button"
                    data-testid={t.testid}
                    onClick={openSmartMic}
                    className="relative -translate-y-3 flex flex-col items-center select-none bg-transparent border-0 outline-none"
                    aria-label="Parla all'Assistente"
                    style={{ touchAction: "manipulation" }}
                  >
                    <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform active:scale-95 bg-[#7C8B6E] ring-4 ring-white shadow-[0_8px_24px_-4px_rgba(124,139,110,0.55)]">
                      <Icon size={26} weight="fill" className="text-white" />
                    </span>
                    <span className="mt-1 text-[11.5px] font-semibold text-[#7C8B6E]">
                      {t.label}
                    </span>
                  </button>
                </li>
              );
            }
            return (
              <li key={t.to} className="flex-1">
                <Link
                  to={t.to}
                  data-testid={t.testid}
                  onClick={() => { nativeHaptic("light").catch(() => {}); }}
                  className="flex flex-col items-center justify-center py-2 gap-0.5"
                  aria-current={active ? "page" : undefined}
                >
                  <Icon
                    size={24}
                    weight={active ? "fill" : "regular"}
                    className={active ? "text-[#1C1C1A]" : "text-[#9A968B]"}
                  />
                  <span className={`text-[11.5px] leading-none ${
                    active ? "text-[#1C1C1A] font-semibold" : "text-[#6B6A65]"
                  }`}>
                    {t.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* MemoRecorder in modalità Smart Mic: trascrizione → classifica
          intent (agenda vs chat) → instrada. Su intent "chat" naviga
          all'Assistente; su "agenda" mostra preview e salva.
          iter v31 r12 — User: «il tasto dell'assistente non farlo partire
          subito. Tu lo schiacci, entri dentro e poi lo facciamo partire».
          Tolto autoStart: ora un tap apre la modal, l'utente preme il
          microfono interno quando è pronto a parlare. */}
      {memoOpen && (
        <MemoRecorder
          open={memoOpen}
          onClose={() => setMemoOpen(false)}
          onCreated={() => { toast.success("Memo salvato"); }}
          routeMode="assistant"
          source="bottombar-smartmic"
          autoStart={false}
        />
      )}
    </>
  );
}
